import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  Award,
  TrendingUp,
  MessageSquare,
  Check,
  Sparkles,
  Crown
} from "lucide-react";

const Addons = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [addons, setAddons] = useState([]);
  const [userJobs, setUserJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const jobIdFromUrl = searchParams.get("job_id");

  useEffect(() => {
    fetchAddons();
    if (user?.role === "recruiter") {
      fetchUserJobs();
    }
  }, [user]);

  const fetchAddons = async () => {
    try {
      const response = await fetch(`${API}/addons`);
      if (response.ok) {
        const data = await response.json();
        setAddons(data);
      }
    } catch (error) {
      console.error("Failed to fetch addons:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs?recruiter_only=true`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUserJobs(data);
        if (jobIdFromUrl) {
          setSelectedJob(jobIdFromUrl);
        }
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const handlePurchase = async (addonId) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL - Use custom Google OAuth
      try {
        const response = await fetch(`${API}/auth/google/url`, {
          credentials: "include",
          headers: { "Origin": window.location.origin }
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.url;
          return;
        }
      } catch (error) {
        console.error("Failed to get Google OAuth URL");
      }
      // Fallback
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    // Check if job selection is needed
    if (addonId.startsWith("promoted_job") && !selectedJob) {
      toast.error("Please select a job to promote");
      return;
    }

    setPurchasing(addonId);
    try {
      const response = await fetch(`${API}/addons/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          addon_id: addonId,
          job_id: addonId.startsWith("promoted_job") ? selectedJob : null,
          origin_url: window.location.origin
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        toast.error("Failed to start checkout");
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setPurchasing(null);
    }
  };

  const getAddonIcon = (addonId) => {
    if (addonId === "featured_employer") return Crown;
    if (addonId.startsWith("promoted_job")) return TrendingUp;
    if (addonId.startsWith("candidate_outreach")) return MessageSquare;
    return Sparkles;
  };

  const getAddonCategory = (addonId) => {
    if (addonId === "featured_employer") return "branding";
    if (addonId.startsWith("promoted_job")) return "promotion";
    if (addonId.startsWith("candidate_outreach")) return "outreach";
    return "other";
  };

  const groupedAddons = addons.reduce((acc, addon) => {
    const category = getAddonCategory(addon.addon_id);
    if (!acc[category]) acc[category] = [];
    acc[category].push(addon);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              ADD-ONS & <span className="text-crimson">BOOSTS</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              Stand out from the competition with premium features and promotional tools
            </p>
          </div>

          {/* Featured Employer Badge */}
          {groupedAddons.branding && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <Crown className="w-6 h-6 text-crimson" />
                EMPLOYER BRANDING
              </h2>
              
              {groupedAddons.branding.map((addon) => (
                <div 
                  key={addon.addon_id}
                  className="bg-gradient-to-r from-crimson/20 to-transparent border border-crimson/50 p-8 relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-64 h-64 bg-crimson/10 rounded-full blur-3xl" />
                  
                  <div className="relative z-10 flex items-start justify-between flex-wrap gap-6">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center">
                        <Award className="w-8 h-8 text-crimson" />
                      </div>
                      <div>
                        <h3 className="font-heading text-2xl font-bold mb-2">{addon.name}</h3>
                        <p className="text-gray-400 mb-4 max-w-lg">{addon.description}</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-300">
                            <Check className="w-4 h-4 text-crimson" />
                            Verified badge on all your job listings
                          </li>
                          <li className="flex items-center gap-2 text-gray-300">
                            <Check className="w-4 h-4 text-crimson" />
                            Enhanced company profile visibility
                          </li>
                          <li className="flex items-center gap-2 text-gray-300">
                            <Check className="w-4 h-4 text-crimson" />
                            Priority in search results
                          </li>
                          <li className="flex items-center gap-2 text-gray-300">
                            <Check className="w-4 h-4 text-crimson" />
                            Valid for 30 days
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-heading text-4xl font-bold text-crimson mb-2">
                        ${addon.price}
                      </div>
                      <p className="text-gray-500 text-sm mb-4">one-time purchase</p>
                      <Button
                        onClick={() => handlePurchase(addon.addon_id)}
                        disabled={purchasing === addon.addon_id}
                        className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8"
                        data-testid={`purchase-${addon.addon_id}`}
                      >
                        {purchasing === addon.addon_id ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "Get Badge"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Job Promotion */}
          {groupedAddons.promotion && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-crimson" />
                PROMOTE YOUR JOBS
              </h2>

              {/* Job Selection */}
              {user?.role === "recruiter" && userJobs.length > 0 && (
                <div className="bg-obsidian-light border border-[#222] p-6 mb-6">
                  <label className="text-gray-400 uppercase tracking-wider text-xs mb-3 block">
                    Select a job to promote
                  </label>
                  <select
                    value={selectedJob || ""}
                    onChange={(e) => setSelectedJob(e.target.value)}
                    className="w-full h-12 px-4 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="job-select"
                  >
                    <option value="">Choose a job posting...</option>
                    {userJobs.map((job) => (
                      <option key={job.job_id} value={job.job_id}>
                        {job.title} {job.promoted ? "(Already Promoted)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {groupedAddons.promotion.map((addon, index) => (
                  <motion.div
                    key={addon.addon_id}
                    className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/50 transition-all"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 bg-crimson/10 flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-crimson" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2">{addon.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{addon.description}</p>
                    <div className="font-heading text-3xl font-bold text-crimson mb-4">
                      ${addon.price}
                    </div>
                    <Button
                      onClick={() => handlePurchase(addon.addon_id)}
                      disabled={purchasing === addon.addon_id || !selectedJob}
                      className="w-full bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider disabled:opacity-50"
                      data-testid={`purchase-${addon.addon_id}`}
                    >
                      {purchasing === addon.addon_id ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Promote Job"
                      )}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Outreach Credits */}
          {groupedAddons.outreach && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-crimson" />
                CANDIDATE OUTREACH
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {groupedAddons.outreach.map((addon, index) => (
                  <motion.div
                    key={addon.addon_id}
                    className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/50 transition-all"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="w-12 h-12 bg-crimson/10 flex items-center justify-center mb-4">
                          <MessageSquare className="w-6 h-6 text-crimson" />
                        </div>
                        <h3 className="font-heading text-xl font-bold mb-2">{addon.name}</h3>
                        <p className="text-gray-400 text-sm mb-4">{addon.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-3xl font-bold text-crimson">
                          ${addon.price}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handlePurchase(addon.addon_id)}
                      disabled={purchasing === addon.addon_id}
                      className="w-full bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                      data-testid={`purchase-${addon.addon_id}`}
                    >
                      {purchasing === addon.addon_id ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Buy Credits"
                      )}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Addons;
